import Sortable from "sortablejs";

export function allocationQuestionConfig(args) {
  const {
    prompt,
    targetRole,
    items,
    langCodes = [],
    multilingualEnabled = false,
    questionTranslations = {},
    defaultLanguage = "en",
  } = args;

  return {
    prompt,
    promptPlaceholder:
      "In the past quarter, what proportion of your time was spent between these areas?",
    targetRole,
    showDescriptionModal: false,
    descriptionModalItemIdx: undefined,
    showDeleteModal: false,
    deleteConfirmText: "",
    items,
    // Multilingual support
    activeLanguage: defaultLanguage,
    defaultLanguage,
    langCodes,
    multilingualEnabled,
    questionTranslations,
    hasTranslation(lang) {
      // Check if prompt has translation
      if (this.questionTranslations[lang]?.prompt) return true;
      // Check if any item has translation (name or description)
      return this.items.some(
        (item) =>
          item.translations?.[lang]?.name ||
          item.translations?.[lang]?.description,
      );
    },
    get allTranslated() {
      if (!this.multilingualEnabled) return true;
      if (this.langCodes.length === 0) return true;

      return this.langCodes.every((lang) => this.hasTranslation(lang));
    },
    init() {
      this.initSortable();
      // Initialize translations and description for items if not present
      this.items.forEach((item) => {
        if (item.description === undefined || item.description === null) {
          item.description = "";
        }
        if (!item.translations) {
          item.translations = {};
        }
        // Loop through each translation to initialize default state
        this.langCodes.forEach((lang) => {
          // Don't overwrite name if it is already translated but description is not translated
          if (!item.translations[lang]) {
            item.translations[lang] = { name: "", description: "" };
          } else if (!item.translations[lang].description) {
            item.translations[lang].description = "";
          }
        });
      });
    },
    displayItemAttribute(item, attribute) {
      if (
        this.activeLanguage !== this.defaultLanguage &&
        item.translations?.[this.activeLanguage]?.[attribute]
      ) {
        return item.translations[this.activeLanguage][attribute];
      }
      return item[attribute] || "";
    },
    isDuplicate(name) {
      return this.items.filter((item) => item.name === name).length > 1;
    },
    addItem() {
      const newItem = { name: "", description: "", translations: {} };
      this.langCodes.forEach((lang) => {
        newItem.translations[lang] = { name: "", description: "" };
      });
      this.items.push(newItem);

      this.$nextTick(() => {
        this.$refs.newOption.focus();
      });
    },
    updateName(event, option) {
      const updatedName = event.target.textContent;
      option.name = updatedName;
    },
    removeItem(idx) {
      this.items.splice(idx, 1);
    },
    openDescriptionModal(idx) {
      this.descriptionModalItemIdx = idx;
      this.showDescriptionModal = true;
    },
    get formValid() {
      return (
        !!this.prompt.trim().length &&
        this.items.length >= 2 &&
        this.items.every((i) => {
          return (
            i.name !== undefined && i.name !== null && !!i.name?.trim()?.length
          );
        }) &&
        !this.items.some((item) => this.isDuplicate(item.name))
      );
    },
    itemPlaceholder(idx) {
      if (idx === 0) {
        return "Feature work";
      } else if (idx === 1) {
        return "Non-feature work";
      } else {
        return `Option ${idx + 1}`;
      }
    },
    reorderArray(array, order) {
      const newArray = new Array(array.length);
      order.forEach((newIndex, originalIndex) => {
        newArray[newIndex] = array[originalIndex];
      });

      return newArray;
    },
    initSortable() {
      const self = this;
      const sortableList = document.getElementById("sortable-list");
      const editableContents = document.querySelectorAll(".editable-content");

      new Sortable(sortableList, {
        animation: 150,
        handle: ".handle",
        onStart() {
          editableContents.forEach((content) =>
            content.setAttribute("contenteditable", "false"),
          );
        },
        onEnd() {
          editableContents.forEach((content) =>
            content.setAttribute("contenteditable", "true"),
          );

          const order = Array.from(
            self.$refs.sortableList.querySelectorAll(".sortable-item"),
          )
            .map((item) => item.dataset.idx)
            .map(Number);

          const itemsToRemove = sortableList.querySelectorAll(".sortable-item");

          // Remove dom elements
          itemsToRemove.forEach((item) => {
            sortableList.removeChild(item);
          });

          // Add dom elements
          const itemsToAdd = self.reorderArray(itemsToRemove, order);
          itemsToAdd.forEach((item) => {
            sortableList.appendChild(item);
          });

          // Update component data
          self.items = order.map((index) => self.items[index]);
        },
      });
    },
  };
}
